import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import FloorMapCard from "components/siteRepo/common/floorMapCard";
import LayerInfo from "components/siteRepo/common/layerInfo";
import Modal from "common/modal";
import ProjectView from "./projectView";
import InputField from "common/customInputField";
import SelectInput from "common/selectInput";
import viewSiteIcon from "svg/viewSite.svg";
import {
  getSiteDetails,
  submitCreateNewSite,
  getAllSiteNames,
  deleteFloor,
  loadCutSheetData,
} from "store/siteRepo/actionCreator";
import { siteDetailsPlaceholders, projectsPlaceHolders } from "utils/common";
import axios from "services/config";
import GenerateSitePdfModal from "../GenerateSitePdfModal";
import {
  confirmBulkUpdateModal,
  updateSelectLayer,
  setSiteData,
  resetEditSiteData,
  loadStoreLaborCodes,
} from "store/slices/siteRepo";
import { api } from "store/slices/api";
import { toast } from "react-toastify";
import "./style.scss";

const SiteDetails = () => {
  const dispatch = useDispatch();
  const { siteId } = useParams();
  const navigate = useNavigate();

  const projectRef = useRef();

  const { viewSite, allSiteNames, showBulkUpdateModal } = useSelector((state) => state.siteRepo);
  const { sub, terrCcn, storeCcn } = useSelector((state) => state.loggedInUser);
  const {
    siteName,
    siteAddress,
    accountName,
    accountNumber,
    storeName,
    storeNumber,
    projectDetailsList,
    createdBy,
    createdTimeStamp,
    updatedBy,
    updatedTimeStamp,
    siteCity,
    siteState,
    siteZip,
    siteStreet,
    parentNumber,
    phoneNumber,
    parentName,
    execName,
    execNumber,
    contactName,
    customerName,
    storePhone,
    accountCity,
    accountStreet,
    accountZip,
    accountState,
    territory,
  } = viewSite;

  const [fetchStoreInfo] = api.useLazyGetStoreInfoQuery();

  const siteBasicDetailsContainerRef = useRef();

  const [stage, setStage] = useState("project");
  const [showDeleteIcon, setShowDeleteIcon] = useState(false);
  const [layerLevelData, setLayerLevelData] = useState({});
  const [isShowDeleteLayerModal, setIsShowDeleteLayerModal] = useState(false);
  const [deleteLayerSelectedData, setDeleteLayerSelectedData] = useState({});
  const [isShowDuplicateSiteModal, setIsShowDuplicateSiteModal] = useState(false);
  const [duplicateSiteData, setDuplicateSiteData] = useState();
  const [isDuplicatingSite, setIsDuplicatingSite] = useState(false);
  const [isShowDuplicateLayerModal, setIsShowDuplicateLayerModal] = useState(false);
  const [duplicateLayerData, setDuplicateLayerData] = useState();
  const [isShowConfirmDuplicateSite, setIsShowConfirmDuplicateSite] = useState(false);
  const [isShowAddFloorPlanModal, setIsShowAddFloorPlanModal] = useState(false);
  const [isAddingFloorPlan, setIsAddingFloorPlan] = useState(false);
  const [isDuplicatingFloorplan, setIsDuplicatingFloorplan] = useState(false);
  const [isShowProjectTabActive, setIsShowProjectTabActive] = useState(false);
  const [addFloorPlanModalData, setAddFloorPlanModalData] = useState({});
  const [projectLevelData, setProjectLevelData] = useState({});
  const [floorPlanNameError, setFloorPlanNameError] = useState("");
  const [floorPlanNameErr, setFloorPlanNameErr] = useState("");
  const [layerNameErr, setLayerNameErr] = useState("");
  const [projectNameErr, setProjectNameErr] = useState("");
  const [showGeneratePdfModal, setShowGeneratePdfModal] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [storeNumValid, setStoreNumValid] = useState("");
  const [terrValid, setTerrValid] = useState("");
  const [siteNameValid, setSiteNameValid] = useState("");
  const [storeData, setStoreData] = useState();

  useEffect(() => {
    if (viewSite?.siteId !== siteId) {
      dispatch(setSiteData());
      dispatch(getSiteDetails(siteId));
    }
  }, [siteId]);

  useEffect(() => {
    const options = [{ id: "", description: "Add New Floor Plan", mprojectId: "" }];
    viewSite?.projectDetailsList?.map((project) => {
      options.push({ id: project.projectId, description: project.name, mprojectId: project.mprojectId });
    });
    setProjectOptions(options);
  }, [viewSite]);

  useEffect(() => {
    if (projectLevelData) {
      const project = viewSite.projectDetailsList?.find((project) => project.projectId === projectLevelData.projectId);
      if (project !== undefined) {
        setProjectLevelData(project);
        const layer = project?.layerInfoList?.find((layer) => layer.layerId === layerLevelData.layerId);
        setLayerLevelData({ ...layer, projectId: project.projectId, projectName: project.name });
      }
    }
  }, [viewSite]);

  useEffect(() => {
    let temp = false;
    viewSite.projectDetailsList?.some((project) => {
      if (project.layerInfoList?.length > 1) {
        temp = true;
      }
    });
    setIsShowProjectTabActive(temp);
  }, [viewSite]);

  useEffect(() => {
    if (storeNumber !== undefined) {
      axios(`/fc-site-repo/store/${storeNumber}/laborCodes?exceptionPrice=0`).then((res) => {
        dispatch(loadStoreLaborCodes({ storeNumber, laborCodes: res.data }));
      });
    }
  }, [storeNumber]);

  const handleConfirm = () => {
    dispatch(confirmBulkUpdateModal(false));
    handleBulkUpdate();
  };

  const handleSiteDetailsExpand = (e) => {
    if (siteBasicDetailsContainerRef.current.classList.contains("d-none")) {
      siteBasicDetailsContainerRef.current.classList.remove("d-none");
      e.target.innerText = "Collapse";
    } else {
      siteBasicDetailsContainerRef.current.classList.add("d-none");
      e.target.innerText = "Expand";
    }
  };

  const handleNavigateToCreateQuote = (siteId) => {
    navigate(`/siteRepo/createQuote/${siteId}#select-floor-plans`);
  };

  const handleNavigateToEdit = async (siteId, type) => {
    await dispatch(resetEditSiteData());
    if (type === "edit") {
      navigate(`/siteRepo/editSite/${siteId}#site-details-editable`);
    } else if (type === "floor") {
      navigate(`/siteRepo/editSite/${siteId}#select-project-editable`);
    }
  };

  const handleNavigateToChangeLog = (siteId) => {
    navigate(`/siteRepo/changeLog/${siteId}`);
  };

  const handleShowMore = (layer, stage, project) => {
    dispatch(loadCutSheetData(project.projectId));
    setStage(stage);
    setLayerLevelData(layer);
    setProjectLevelData(project);
  };

  const handleShowMoreProj = (layer, stage, project) => {
    window.scrollTo(0, 0);
    setStage(stage);
    setProjectLevelData(project);
  };

  const changeStage = (stageValue) => {
    setStage(stageValue);
  };

  const handleShowDuplicateSiteModal = (isShow) => {
    setIsShowDuplicateSiteModal(isShow);
    setIsDuplicatingSite(false);
    if (isShow) {
      setDuplicateSiteData({ ...viewSite, editableStoreNum: storeCcn, editableTerrNum: terrCcn });
      dispatch(getAllSiteNames());
      fetchStoreInfo({ storeNumber: storeCcn }).then((res) => {
        if (res.data.length) {
          setStoreData(res.data[0]);
        }
      });
    } else {
      setDuplicateSiteData({});
    }
  };

  const handleShowDuplicateLayerModal = (isShow) => {
    setIsShowDuplicateLayerModal(isShow);
    setIsDuplicatingFloorplan(false);
    if (!isShow) {
      setDuplicateLayerData({});
    }
  };

  const onDuplicateLayerFieldsChange = (e, val) => {
    const { id, value } = e.target;
    if (id === "layerName") {
      setDuplicateLayerData({ ...duplicateLayerData, name: value });
    } else if (id === "floorPlanName") {
      setDuplicateLayerData({ ...duplicateLayerData, floorPlanName: value });
    } else {
      setDuplicateLayerData({
        ...duplicateLayerData,
        [id]: val.description,
        projectId: val.id,
        mprojectId: val.mprojectId,
      });
    }
  };

  const onDuplicateSiteFieldsChange = (e) => {
    const { id, value } = e.target;
    setDuplicateSiteData({ ...duplicateSiteData, [id]: value });
    if (id === "editableStoreNum") {
      setTimeout(() => {
        fetchStoreInfo({
          storeNumber: value,
        }).then((res) => {
          if (res.data.length) {
            setStoreData(res.data[0]);
          }
        });
      }, 2000);
    }
  };

  const confirmDuplicateSite = () => {
    if (isDuplicatingSite) {
      return;
    }
    const {
      siteName,
      siteAddress,
      siteCity,
      siteState,
      accountNumber,
      accountName,
      siteZip,
      siteStreet,
      projectDetailsList,
      parentNumber,
      editableStoreNum,
      editableTerrNum,
      phoneNumber,
      parentName,
      execName,
      execNumber,
      contactName,
      customerName,
      accountCity,
      accountStreet,
      accountZip,
      accountState,
      laborTaxIndicator,
    } = duplicateSiteData;
    const duplicateSiteDataObj = {
      siteName,
      siteAddress,
      siteCity,
      siteState,
      accountNumber,
      accountName,
      storeNumber: editableStoreNum,
      storeName: storeData.storeName,
      siteZip,
      siteStreet,
      createdBy: sub,
      territory: editableTerrNum,
      parentNumber,
      phoneNumber,
      parentName,
      execName,
      execNumber,
      contactName,
      customerName,
      storePhone: storeData.storePhoneNumber,
      accountCity,
      accountStreet,
      accountZip,
      accountState,
      laborTaxIndicator,
      projectDetailsList: projectDetailsList.map((project) => {
        const { mprojectId, name, projectId, floorPlanNotes, specialInstructions, installerNotes } = project;
        return {
          mprojectId,
          projectId: "",
          duplicateProjectId: projectId,
          name,
          updateType: "old",
          createdBy: sub,
          floorPlanNotes: floorPlanNotes,
          specialInstructions: specialInstructions,
          installerNotes: installerNotes,
          layerInfoList: project.layerInfoList.map((layer) => {
            const { name, index, shapeId, layerId } = layer;
            return {
              duplicateLayerId: layerId,
              layerId: "",
              name,
              shapeId,
              createdBy: sub,
              pictureData: "",
              index,
              rooms: layer.rooms
                ? layer.rooms.map((room) => {
                    const { area, perimeter, name, length, shapeId, width, manualRoom, type } = room;
                    return {
                      roomId: "",
                      layerId: "",
                      name,
                      perimeter,
                      area,
                      length,
                      shapeId,
                      width,
                      type,
                      manualRoom,
                      products: room.products
                        ? room.products.map((product) => {
                            const {
                              id,
                              netArea,
                              quantity,
                              wasteRatio,
                              sellByBox,
                              unitsPerBox,
                              boxName,
                              taxable,
                              shapeId,
                              width,
                              boxCostPrice,
                              boxSalesPrice,
                              color,
                              costPrice,
                              desc,
                              manufacturer,
                              salesPrice,
                              trade,
                              type,
                              unit,
                              vendor,
                              vendorSku,
                              actualUsage,
                              modWaste,
                              modActualUsage,
                              salesNumber,
                              categoryCode,
                              linearLength,
                              basePerimeter,
                              waste,
                              siteLaborCodes,
                            } = product;

                            return {
                              roomInfoId: "",
                              roomId: "",
                              netArea,
                              quantity,
                              wasteRatio,
                              sellByBox,
                              unitsPerBox,
                              id,
                              boxName,
                              taxable,
                              shapeId,
                              width,
                              boxCostPrice,
                              boxSalesPrice,
                              color,
                              costPrice,
                              desc,
                              manufacturer,
                              salesPrice,
                              trade,
                              type,
                              unit,
                              vendor,
                              vendorSku,
                              actualUsage,
                              modWaste,
                              modActualUsage,
                              salesNumber,
                              categoryCode,
                              linearLength,
                              basePerimeter,
                              waste,
                              siteLaborCodes: siteLaborCodes?.length
                                ? siteLaborCodes.map((laborItem) => {
                                    const { laborCodeId, quantity, isTaxable } = laborItem;
                                    return {
                                      isTaxable,
                                      laborCodeId,
                                      quantity,
                                    };
                                  })
                                : [],
                            };
                          })
                        : [],
                    };
                  })
                : [],
            };
          }),
        };
      }),
    };

    let valid = true;
    setSiteNameValid("");
    setTerrValid("");
    setStoreNumValid("");
    if (!siteName || siteName == "") {
      valid = false;
      setSiteNameValid("Please enter Site Name");
    } else if (!editableStoreNum || editableStoreNum == "") {
      valid = false;
      setStoreNumValid("Please enter Store Number");
    } else if (!editableTerrNum || editableTerrNum == "") {
      valid = false;
      setTerrValid("Please enter Territory Number");
    }

    if (valid) {
      setIsDuplicatingSite(true);

      toast
        .promise(dispatch(submitCreateNewSite(duplicateSiteDataObj)), {
          pending: "Duplicating Site",
          success: "Site duplicated successfully",
          error: "Could not duplicate site, please try again.",
        })
        .then((res) => {
          handleShowDuplicateSiteModal(false);
          setIsShowConfirmDuplicateSite(false);
          navigate(`/siteRepo/site/${res.siteId}`);
          dispatch(getSiteDetails(res.siteId));
        })
        .catch(() => {})
        .finally(() => setIsDuplicatingSite(false));
    }
  };

  const duplicateSiteSubmit = (isShow) => {
    if (isShow) {
      if (allSiteNames?.includes(duplicateSiteData.siteName)) {
        setIsShowConfirmDuplicateSite(true);
      } else {
        setIsShowConfirmDuplicateSite(false);
        confirmDuplicateSite();
      }
    } else {
      setIsShowConfirmDuplicateSite(false);
    }
  };

  const confirmDuplicateLayer = () => {
    if (isDuplicatingFloorplan) {
      return;
    }
    const duplicateLayerReqObj = {
      ...viewSite,
      updatedBy: sub,
      updatedTimeStamp: new Date(),
      siteId,
      siteName,
      siteAddress,
      siteCity,
      siteState,
      siteZip,
      siteStreet,
      createdBy: sub,
      accountName,
      accountNumber,
      storeName,
      storeNumber,
      parentNumber,
      phoneNumber,
      parentName,
      execName,
      execNumber,
      contactName,
      customerName,
      storePhone,
      accountCity,
      accountStreet,
      accountZip,
      accountState,
      projectDetailsList: [
        {
          duplicateProjectId:
            duplicateLayerData.projectName === "Add New Floor Plan" ? duplicateLayerData.duplicateProjectId : "",
          mprojectId: duplicateLayerData.mprojectId,
          projectId: duplicateLayerData.projectId,
          name:
            duplicateLayerData.projectName === "Add New Floor Plan"
              ? duplicateLayerData.floorPlanName
              : duplicateLayerData.projectName,
          updateType: "old",
          floorPlanNotes: duplicateLayerData.floorPlanNotes,
          specialInstructions: duplicateLayerData.specialInstructions,
          installerNotes: duplicateLayerData.installerNotes,
          layerInfoList: [
            {
              duplicateLayerId: duplicateLayerData.layerId,
              name: duplicateLayerData.name,
              createdBy: sub,
              projectName:
                duplicateLayerData.projectName === "Add New Floor Plan"
                  ? duplicateLayerData.floorPlanName
                  : duplicateLayerData.projectName,
              rooms: duplicateLayerData.rooms
                ? duplicateLayerData.rooms.map((roomItem) => {
                    return {
                      ...roomItem,
                      createdBy: sub,
                      roomId: "",
                      layerId: "",
                      products: roomItem.products?.length
                        ? roomItem.products.map((prodItem) => {
                            return {
                              ...prodItem,
                              roomId: "",
                              roomInfoId: "",
                              createdBy: sub,
                              siteLaborCodes: prodItem.siteLaborCodes?.length
                                ? prodItem.siteLaborCodes.map((laborCodeItem) => {
                                    const { laborCodeId, quantity, isTaxable } = laborCodeItem;
                                    return {
                                      isTaxable,
                                      laborCodeId,
                                      quantity,
                                      createdBy: sub,
                                    };
                                  })
                                : [],
                            };
                          })
                        : [],
                    };
                  })
                : [],
            },
          ],
        },
      ],
    };

    let valid = true;
    setFloorPlanNameError("");
    if (
      duplicateLayerData.projectName === "Add New Floor Plan" &&
      (!duplicateLayerData.floorPlanName || duplicateLayerData.floorPlanName == "")
    ) {
      valid = false;
      setFloorPlanNameError("Please enter a Name");
    }

    if (valid) {
      setIsDuplicatingFloorplan(true);

      toast
        .promise(dispatch(submitCreateNewSite(duplicateLayerReqObj)), {
          pending: "Duplicating Floor",
          success: "Floor duplicated successfully",
          error: "Could not duplicate floor, please try again.",
        })
        .then((res) => {
          handleShowDuplicateLayerModal(false);
          dispatch(getSiteDetails(res.siteId));
        })
        .catch(() => {})
        .finally(() => setIsDuplicatingFloorplan(false));
    }
  };

  const onDuplicateClick = (layerData) => {
    setDuplicateLayerData({
      ...layerData,
      duplicateProjectId: layerData.projectId,
      projectName: "Add New Floor Plan",
      projectId: "",
      mprojectId: "",
    });
    handleShowDuplicateLayerModal(true);
  };

  const handleAddFloorPlanModal = (isShow) => {
    setIsShowAddFloorPlanModal(isShow);
    setIsAddingFloorPlan(false);
    setAddFloorPlanModalData({});
  };

  const confirmAddFloorPlan = () => {
    if (isAddingFloorPlan) {
      return;
    }
    const {
      siteName,
      siteAddress,
      siteCity,
      siteState,
      accountNumber,
      accountName,
      storeNumber,
      storeName,
      siteZip,
      siteStreet,
      parentNumber,
      phoneNumber,
      parentName,
      execName,
      execNumber,
      contactName,
      customerName,
      storePhone,
      accountCity,
      accountStreet,
      accountZip,
      accountState,
      territory,
      laborTaxIndicator,
    } = viewSite;
    const addFloorPlanReqObj = {
      siteId,
      siteName,
      siteAddress,
      siteCity,
      siteState,
      accountNumber,
      accountName,
      storeNumber,
      storeName,
      siteZip,
      siteStreet,
      parentNumber,
      phoneNumber,
      parentName,
      execName,
      execNumber,
      contactName,
      customerName,
      storePhone,
      accountCity,
      accountStreet,
      accountZip,
      accountState,
      territory,
      laborTaxIndicator,
      projectDetailsList: [
        {
          mprojectId: addFloorPlanModalData.mprojectId ? addFloorPlanModalData.mprojectId : "",
          projectId: addFloorPlanModalData.projectId ? addFloorPlanModalData.projectId : "",
          name:
            addFloorPlanModalData.projectName === "Add New Floor Plan"
              ? addFloorPlanModalData.floorPlanName
              : addFloorPlanModalData.projectName,
          createdBy: sub,
          updateType: "old",
          layerInfoList: [
            {
              name: addFloorPlanModalData.layerName,
              createdBy: sub,
              projectName:
                addFloorPlanModalData.projectName === "Add New Floor Plan"
                  ? addFloorPlanModalData.floorPlanName
                  : addFloorPlanModalData.projectName,
              rooms: [],
            },
          ],
        },
      ],
    };

    let valid = true;
    setFloorPlanNameErr("");
    setLayerNameErr("");
    setProjectNameErr("");
    if (!addFloorPlanModalData.projectName || addFloorPlanModalData.projectName == "") {
      valid = false;
      setProjectNameErr("Please select a Floor Plan");
    } else if (!addFloorPlanModalData.layerName || addFloorPlanModalData.layerName == "") {
      valid = false;
      setLayerNameErr("Please enter a Name");
    } else if (
      addFloorPlanModalData.projectName === "Add New Floor Plan" &&
      (!addFloorPlanModalData.floorPlanName || addFloorPlanModalData.floorPlanName == "")
    ) {
      valid = false;
      setFloorPlanNameErr("Please enter a Name");
    }

    if (valid) {
      setIsAddingFloorPlan(true);

      toast
        .promise(dispatch(submitCreateNewSite(addFloorPlanReqObj)), {
          pending: "Adding Floorplan",
          success: "Floorplan added successfully",
          error: "Could not add floorplan, please try again.",
        })
        .then((res) => {
          handleAddFloorPlanModal(false);
          dispatch(getSiteDetails(res.siteId));
        })
        .catch(() => {})
        .finally(() => setIsAddingFloorPlan(false));
    }
  };

  const onAddFloorFieldsChange = (e, val) => {
    const { id, value } = e.target;
    if (id === "layerName" || id === "floorPlanName") {
      setAddFloorPlanModalData({ ...addFloorPlanModalData, [id]: value });
    } else {
      setAddFloorPlanModalData({
        ...addFloorPlanModalData,
        [id]: val.description,
        projectId: val.id,
        mprojectId: val.mprojectId,
      });
    }
  };

  const handleRemoveLayer = (selectionData) => {
    const { type, data } = selectionData;
    if (type === "layer") {
      if (data.layer?.layerId) {
        const deleteFloorRequestObject = {
          layerId: data.layer?.layerId,
          projectId: data.project.projectId,
          siteId,
          updatedBy: sub,
        };
        dispatch(deleteFloor(data.project.projectId, deleteFloorRequestObject, true));
      } else {
        dispatch(
          updateSelectLayer({
            selectLayerProjectIndex: data.projectIndex,
            selectLayerLayerIndex: data.layerIndex,
            isEdit: true,
          }),
        );
      }
    }

    handleDeleteLayerModal(false);
    setShowDeleteIcon(false);
  };

  const showRemoveFloorIcon = () => {
    setShowDeleteIcon(true);
  };

  const handleDeleteLayerModal = (isShow) => {
    if (isShow) {
      setIsShowDeleteLayerModal(true);
    } else {
      setIsShowDeleteLayerModal(false);
    }
  };

  const handleDeleteLayer = (data) => {
    setDeleteLayerSelectedData(data);
    setIsShowDeleteLayerModal(true);
  };

  const handleBulkUpdate = () => {
    navigate(`/siteRepo/bulkUpdateSite/${siteId}`);
  };

  return (
    <>
      {stage === "project" && (
        <div className="view-site__container">
          <div className="container-fluid search-form-container heading-section">
            <div className="search-form-container__title d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <img src={viewSiteIcon} alt={"view-site-icon"} />
                <span className="search-form-container__title--text">{`Site ID: ${siteId}`}</span>
              </div>
              <div className="dropdown flex-grow-1 text-end d-flex justify-content-end">
                <Button
                  variant="outline-primary"
                  className="d-none d-md-flex "
                  id={"actions_more"}
                  data-bs-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  Site Actions
                </Button>
                <div
                  className="dropdown-menu mobile-data__row__column__item__top__right__menu site-details-header-dropdown"
                  aria-labelledby={"actions_more"}
                >
                  <div onClick={handleSiteDetailsExpand} className="d-flex d-md-none">
                    Expand
                  </div>
                  <hr className="d-flex d-md-none" />
                  {((territory === terrCcn && territory !== "800100") || storeCcn === storeNumber) && (
                    <Button
                      variant="link"
                      className="text-decoration-none"
                      onClick={() => handleNavigateToEdit(siteId, "edit")}
                    >
                      Edit Site
                    </Button>
                  )}
                  <Button
                    variant="link"
                    className="text-decoration-none"
                    onClick={() => handleShowDuplicateSiteModal(true)}
                  >
                    Duplicate Site
                  </Button>
                  <Button
                    variant="link"
                    className="text-decoration-none"
                    onClick={() => handleNavigateToChangeLog(siteId)}
                  >
                    Change Log
                  </Button>
                  <Button
                    variant="link"
                    className="text-decoration-none text-nowrap"
                    onClick={() => setShowGeneratePdfModal(true)}
                  >
                    Generate Site PDF
                  </Button>
                </div>
              </div>
            </div>
            <div className="d-flex position-relative" ref={siteBasicDetailsContainerRef}>
              {siteName ? (
                <div className="d-flex flex-column info-item justify-content-start">
                  <div className="info-item__label text-nowrap">Site Name</div>
                  <div className="info-item__value text-start">{siteName}</div>
                </div>
              ) : (
                siteDetailsPlaceholders()
              )}
              {siteAddress ? (
                <div className="d-flex flex-column info-item justify-content-start">
                  <div className="info-item__label text-nowrap">Site Address</div>
                  <div className="info-item__value text-start site-address">{siteAddress}</div>
                </div>
              ) : (
                siteDetailsPlaceholders()
              )}
              {accountNumber && (
                <div className="d-flex flex-column info-item justify-content-start">
                  <div className="info-item__label text-nowrap">Account Number</div>
                  <div className="info-item__value text-start">{accountNumber}</div>
                </div>
              )}
              {accountName && (
                <div className="d-flex flex-column info-item justify-content-start">
                  <div className="info-item__label text-nowrap">Account Name</div>
                  <div className="info-item__value text-start">{accountName}</div>
                </div>
              )}

              {storeNumber && (
                <div className="d-flex flex-row flex-column info-item justify-content-start">
                  <div className="info-item__label text-nowrap">Store Number</div>
                  <div className="info-item__value text-start">{storeNumber}</div>
                </div>
              )}
              {storeName && (
                <div className="d-flex flex-column info-item justify-content-start">
                  <div className="info-item__label text-nowrap">Store Name</div>
                  <div className="info-item__value text-start">{storeName}</div>
                </div>
              )}
            </div>
            <div
              className="d-flex flex-row mt-5 mt-md-4 overflow-lg-auto position-relative site-date-time-info justify-content-start"
              ref={siteBasicDetailsContainerRef}
            >
              {(parentName || parentNumber) && (
                <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start">
                  <div className="info-item__label">National Parent</div>
                  <div className="info-item__value text-end text-md-start">
                    {parentNumber} - {parentName}
                  </div>
                </div>
              )}

              {createdBy && createdTimeStamp && (
                <div className="date-sty">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="label">Created By: </div>
                    <div className="value">{createdBy}</div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="label">Created Date: </div>
                    <div className="value">{new Date(createdTimeStamp).toLocaleDateString("en-US")}</div>
                  </div>
                </div>
              )}
              {updatedBy && updatedTimeStamp && (
                <div className="date-sty">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="label">Updated By: </div>
                    <div className="value">{updatedBy}</div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="label">Updated Date: </div>
                    <div className="value">{new Date(updatedTimeStamp).toLocaleDateString("en-US")}</div>
                  </div>
                </div>
              )}

              {territory && (
                <div className="date-sty">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="label">Territory Number: </div>
                    <div className="value">{territory}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end align-items-center mb-4">
            <div className="dropdown flex-grow-1 d-md-flex flex-column flex-md-row justify-content-md-end">
              {((territory === terrCcn && territory !== "800100") || storeCcn === storeNumber) && (
                <>
                  <Button variant="outline-primary" onClick={handleBulkUpdate}>
                    Bulk Update
                  </Button>
                  <Button variant="outline-primary" onClick={showRemoveFloorIcon}>
                    Delete Floor Plan
                  </Button>
                  <Button variant="outline-primary" id="actions_more" data-bs-toggle="dropdown" aria-haspopup="true">
                    Add Floor Plan
                  </Button>
                  <div
                    className="dropdown-menu mobile-data__row__column__item__top__right__menu site-details-header-dropdown"
                    aria-labelledby={"actions_more"}
                  >
                    <div onClick={handleSiteDetailsExpand} className="d-none">
                      Expand
                    </div>
                    <Button
                      variant="link"
                      className="text-decoration-none"
                      onClick={() => handleAddFloorPlanModal(true)}
                    >
                      Manual Add
                    </Button>
                    <Button
                      variant="link"
                      className="text-decoration-none mt-2"
                      onClick={() => handleNavigateToEdit(siteId, "floor")}
                    >
                      Import
                    </Button>
                  </div>
                </>
              )}
              <Button className="me-0" variant="outline-primary" onClick={() => handleNavigateToCreateQuote(siteId)}>
                Create Quote
              </Button>
            </div>
          </div>

          <div className="site-info-main-content">
            {!isShowProjectTabActive ? (
              <div className="container-fluid search-form-container">
                <div className="search-form-container__title d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <span className="search-form-container__title--text">Floor Plans (Projects)</span>
                  </div>
                </div>

                <div className="row selected-projects">
                  <div className="d-flex flex-column flex-md-row flex-wrap">
                    {projectDetailsList && projectDetailsList.length
                      ? projectDetailsList?.map((project) => {
                          return (
                            project.layerInfoList?.length &&
                            project.layerInfoList.map((layerItem, index) => {
                              return (
                                <FloorMapCard
                                  key={index}
                                  layerIndex={layerItem.elementIndex}
                                  layerObject={layerItem}
                                  handleSelection={() => {}}
                                  project={project}
                                  isSiteDetails={showDeleteIcon}
                                  layerView={true}
                                  removeLayerAction={handleDeleteLayer}
                                  isSelectable={false}
                                  isShowMore={true}
                                  onShowMoreClick={handleShowMore}
                                  onDuplicateClick={onDuplicateClick}
                                  territoryAccess={
                                    (territory === terrCcn && territory !== "800100") || storeCcn === storeNumber
                                  }
                                />
                              );
                            })
                          );
                        })
                      : ""}
                    {projectDetailsList && projectDetailsList.length === 0 ? (
                      <div className="d-flex w-100 align-items-center justify-content-center">
                        <div className="alert alert-warning no-rooms-info mt-4" role="alert">
                          No Floorplans available. Consider adding one!
                        </div>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                  {!projectDetailsList && projectsPlaceHolders()}
                </div>
              </div>
            ) : (
              <div className="">
                <ProjectView
                  viewSiteData={viewSite}
                  handleDeleteLayer={handleDeleteLayer}
                  onDuplicateClick={onDuplicateClick}
                  handleShowMore={handleShowMore}
                  handleShowMoreProj={handleShowMoreProj}
                  showDeleteIcon={showDeleteIcon}
                  territoryAccess={(territory === terrCcn && territory !== "800100") || storeCcn === storeNumber}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {stage === "layer" && (
        <LayerInfo
          changeStage={changeStage}
          project={projectLevelData}
          handleShowMore={handleShowMore}
          layer={layerLevelData}
          scrollPosition={{ scrollX: 0, scrollY: 0 }}
        />
      )}

      {isShowDuplicateSiteModal && (
        <Modal
          title={"Duplicate Site"}
          handleSave={duplicateSiteSubmit}
          handleCancel={() => handleShowDuplicateSiteModal(false)}
          size={"lg"}
          sectionName={""}
          closeButtonText={"Cancel"}
          saveButtonText={"Duplicate"}
          isSaveBtnDisabled={isDuplicatingSite}
        >
          <>
            <div className="container-fluid p-2 border-0 m-0">
              <div className="search-form-container__fields">
                <form className="search-form-container__fields__form">
                  <div className="row align-items-center">
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        id="siteName"
                        label="Site Name"
                        placeholder={"Enter Site Name"}
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={duplicateSiteData.siteName || ""}
                        onChange={(e) => onDuplicateSiteFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                        errorMsg={siteNameValid}
                      />
                    </div>
                  </div>
                  <div className="row align-items-center">
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        id="editableStoreNum"
                        label="Store Number"
                        placeholder={"Enter Site Name"}
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={duplicateSiteData.editableStoreNum || ""}
                        onChange={(e) => onDuplicateSiteFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                        errorMsg={storeNumValid}
                      />
                    </div>
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        id="editableTerrNum"
                        label="Territory Number"
                        placeholder={"Enter Site Name"}
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={duplicateSiteData.editableTerrNum || ""}
                        onChange={(e) => onDuplicateSiteFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                        errorMsg={terrValid}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </>
        </Modal>
      )}
      {isShowDuplicateLayerModal && (
        <Modal
          title={"Duplicate Floor Plan"}
          handleSave={confirmDuplicateLayer}
          handleCancel={() => handleShowDuplicateLayerModal(false)}
          size={"lg"}
          sectionName={""}
          closeButtonText={"Cancel"}
          saveButtonText={"Duplicate"}
          isSaveBtnDisabled={isDuplicatingFloorplan}
        >
          <>
            <div className="container-fluid p-2 border-0 m-0">
              <div className="search-form-container__fields">
                <form className="search-form-container__fields__form">
                  <div className="row align-items-center">
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <SelectInput
                        inputRef={projectRef}
                        id="projectName"
                        isPrc={false}
                        options={projectOptions}
                        label={"Select Floor Plan"}
                        defaultValue={"Select a Project"}
                        value={duplicateLayerData.projectName || ""}
                        handleSelectInput={(e, value) => onDuplicateLayerFieldsChange(e, value)}
                      />
                    </div>
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        id="layerName"
                        label="Floor Name"
                        placeholder={"Enter Floor Name"}
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={duplicateLayerData.name || ""}
                        onChange={(e) => onDuplicateLayerFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                      />
                    </div>

                    {duplicateLayerData.projectName === "Add New Floor Plan" && (
                      <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                        <InputField
                          type="text"
                          id="floorPlanName"
                          label="Floor Plan Name"
                          placeholder={"Enter Floor Plan Name"}
                          fieldClasses="form-control search-form-container__fields__form-group__item"
                          value={duplicateLayerData.floorPlanName || ""}
                          onChange={(e) => onDuplicateLayerFieldsChange(e)}
                          disabled={false}
                          isRequired={false}
                          errorMsg={floorPlanNameError}
                        />
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </>
        </Modal>
      )}
      {isShowConfirmDuplicateSite && (
        <Modal
          title={"Are you Sure?"}
          handleSave={() => confirmDuplicateSite()}
          handleCancel={() => duplicateSiteSubmit(false)}
          size={"lg"}
          sectionName={""}
          closeButtonText={"Cancel"}
          saveButtonText={"Continue"}
          isSaveBtnDisabled={isDuplicatingSite}
        >
          <>
            <div className="container-fluid p-2 border-0 m-0">
              <p>
                There is already a site named <b>{duplicateSiteData.siteName}</b>. Do you want to continue with the same
                name?
              </p>
            </div>
          </>
        </Modal>
      )}

      {isShowDeleteLayerModal && (
        <Modal
          title={"Are you Sure?"}
          handleSave={() => handleRemoveLayer({ ...deleteLayerSelectedData })}
          handleCancel={() => handleDeleteLayerModal(false)}
          size={"lg"}
          sectionName={""}
          bodyClass={"account-lookup-search-form"}
          closeButtonText={"Cancel"}
          saveButtonText={"Remove"}
          saveButtonClass={"btn-primary remove"}
        >
          <>
            <div className="container-fluid search-form-container border-0 m-0">
              <p>
                Are you sure you want to remove the{" "}
                <b>
                  {deleteLayerSelectedData.data.layerName} - {deleteLayerSelectedData.data.projectName}
                </b>{" "}
                unit from the selected list?
              </p>
            </div>
          </>
        </Modal>
      )}

      {isShowAddFloorPlanModal && (
        <Modal
          title={"Add Floor Plan"}
          handleSave={confirmAddFloorPlan}
          handleCancel={() => handleAddFloorPlanModal(false)}
          size={"lg"}
          sectionName={""}
          closeButtonText={"Cancel"}
          saveButtonText={"Add"}
          isSaveBtnDisabled={isAddingFloorPlan}
        >
          <>
            <div className="container-fluid p-2 border-0 m-0">
              <div className="search-form-container__fields">
                <form className="search-form-container__fields__form">
                  <div className="row align-items-center">
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <SelectInput
                        inputRef={projectRef}
                        id="projectName"
                        isPrc={false}
                        options={projectOptions}
                        label={"Select Floor Plan"}
                        defaultValue={"Select a Project"}
                        value={addFloorPlanModalData.projectName || ""}
                        handleSelectInput={(e, value) => onAddFloorFieldsChange(e, value)}
                        errorMsg={projectNameErr}
                      />
                    </div>
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        id="layerName"
                        label="Floor Name"
                        placeholder={"Enter Floor Name"}
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={addFloorPlanModalData.layerName || ""}
                        onChange={(e) => onAddFloorFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                        errorMsg={layerNameErr}
                      />
                    </div>

                    {addFloorPlanModalData.projectName === "Add New Floor Plan" && (
                      <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                        <InputField
                          type="text"
                          id="floorPlanName"
                          label="Floor Plan Name"
                          placeholder={"Enter Floor Plan Name"}
                          fieldClasses="form-control search-form-container__fields__form-group__item"
                          value={addFloorPlanModalData.floorPlanName || ""}
                          onChange={(e) => onAddFloorFieldsChange(e)}
                          disabled={false}
                          isRequired={false}
                          errorMsg={floorPlanNameErr}
                        />
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </>
        </Modal>
      )}
      {showGeneratePdfModal && (
        <GenerateSitePdfModal floors={viewSite.projectDetailsList} onClose={() => setShowGeneratePdfModal(false)} />
      )}

      {showBulkUpdateModal && (
        <Modal
          handleSave={handleConfirm}
          handleCancel={() => dispatch(confirmBulkUpdateModal(false))}
          size={"lg"}
          bodyClass={"account-lookup-search-form p-5"}
          closeButtonText={"No"}
          saveButtonText={"Yes"}
        >
          <>
            <div>
              <p>
                You just created a site with a floor plan(s) imported from Measure Square. Would you like to perform a
                bulk update to add Sherwin-Williams Products and Labor Codes now?
              </p>
            </div>
          </>
        </Modal>
      )}
    </>
  );
};

export default SiteDetails;
